// Work-email classification for the Content Prompt Builder gate. The domain lists and the MX
// lookup are ported from sked-marketing-site apps/web/src/lib/plan-rec/validate-email.ts.
// Unlike plan-rec, a free-mail address is accepted and tagged rather than rejected, and a
// failed MX lookup is accepted and tagged so a DNS hiccup never blocks a real business email.
// The sync check runs in the browser and on the Worker; the MX lookup is Worker only.

export type EmailType = "business" | "freemail";
export type MxStatus = "ok" | "lookup_failed";

export type SyncEmailCheck =
  | { ok: true; email: string; domain: string; type: EmailType }
  | { ok: false; reason: "syntax" | "disposable" };

export type EmailClassification =
  | { ok: true; email: string; domain: string; type: EmailType; mx: MxStatus }
  | { ok: false; reason: "syntax" | "disposable" | "mx_missing" };

/** Common consumer email providers. */
export const FREEMAIL_DOMAINS: ReadonlySet<string> = new Set([
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "yahoo.co.uk",
  "yahoo.com.au",
  "yahoo.ca",
  "yahoo.fr",
  "yahoo.de",
  "yahoo.es",
  "ymail.com",
  "outlook.com",
  "outlook.co.uk",
  "outlook.com.au",
  "hotmail.com",
  "hotmail.co.uk",
  "hotmail.com.au",
  "hotmail.fr",
  "hotmail.de",
  "live.com",
  "live.co.uk",
  "live.com.au",
  "msn.com",
  "aol.com",
  "aim.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "proton.me",
  "protonmail.com",
  "pm.me",
  "zoho.com",
  "mail.com",
  "yandex.com",
  "yandex.ru",
  "gmx.com",
  "gmx.net",
  "gmx.de",
  "fastmail.com",
  "fastmail.fm",
  "tutanota.com",
  "tutanota.de",
  "tuta.io",
  "mail.ru",
  "qq.com",
  "163.com",
  "126.com",
  "naver.com",
  "rediffmail.com",
  "rocketmail.com",
  "btinternet.com",
  "bigpond.com",
  "bigpond.net.au",
  "optusnet.com.au",
]);

/** Known disposable or temporary email services. */
export const DISPOSABLE_DOMAINS: ReadonlySet<string> = new Set([
  "mailinator.com",
  "guerrillamail.com",
  "guerrillamail.net",
  "guerrillamail.org",
  "sharklasers.com",
  "10minutemail.com",
  "10minutemail.net",
  "tempmail.com",
  "temp-mail.org",
  "throwawaymail.com",
  "yopmail.com",
  "trashmail.com",
  "trashmail.de",
  "trashmail.net",
  "getairmail.com",
  "dispostable.com",
  "mintemail.com",
  "moakt.com",
  "anonbox.net",
  "spambox.us",
  "tempinbox.com",
  "fakemailgenerator.com",
  "mohmal.com",
  "emkei.cz",
  "mailcatch.com",
  "tempr.email",
  "deadaddress.com",
  "spam4.me",
  "binkmail.com",
  "bouncr.com",
  "discardmail.com",
]);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Trimmed and lowercased: the form every store, hash and comparison uses. */
export function normaliseEmail(input: string): string {
  return input.trim().toLowerCase();
}

/** No network. Runs in the browser and on the Worker. */
export function classifyEmailSync(input: string): SyncEmailCheck {
  const email = normaliseEmail(input);
  if (!EMAIL_RE.test(email)) return { ok: false, reason: "syntax" };
  const domain = email.split("@")[1];
  if (!domain) return { ok: false, reason: "syntax" };
  if (DISPOSABLE_DOMAINS.has(domain)) return { ok: false, reason: "disposable" };
  return { ok: true, email, domain, type: FREEMAIL_DOMAINS.has(domain) ? "freemail" : "business" };
}

interface DohResponse {
  Status: number;
  Answer?: Array<{ type: number; data: string }>;
}

const DNS_NOERROR = 0;
const DNS_NXDOMAIN = 3;
const DNS_TYPE_MX = 15;

/** Cloudflare DoH MX lookup. "missing" only when DNS answered that there is no mail domain. */
export async function lookupMx(
  domain: string,
  fetchImpl: typeof fetch = fetch,
): Promise<"found" | "missing" | "lookup_failed"> {
  try {
    const url = `https://cloudflare-dns.com/dns-query?name=${encodeURIComponent(domain)}&type=MX`;
    const res = await fetchImpl(url, {
      headers: { accept: "application/dns-json" },
      signal: AbortSignal.timeout(2000),
    });
    if (!res.ok) return "lookup_failed";
    const json = (await res.json()) as DohResponse;
    if (json.Status === DNS_NXDOMAIN) return "missing";
    if (json.Status !== DNS_NOERROR) return "lookup_failed";
    const mxCount = (json.Answer ?? []).filter((a) => a.type === DNS_TYPE_MX).length;
    return mxCount > 0 ? "found" : "missing";
  } catch {
    return "lookup_failed";
  }
}

/** Server-side classification: the sync check, then MX for anything not a known provider. */
export async function classifyEmail(
  input: string,
  fetchImpl: typeof fetch = fetch,
): Promise<EmailClassification> {
  const sync = classifyEmailSync(input);
  if (!sync.ok) return sync;
  if (sync.type === "freemail") return { ...sync, mx: "ok" };
  const mx = await lookupMx(sync.domain, fetchImpl);
  if (mx === "missing") return { ok: false, reason: "mx_missing" };
  return { ...sync, mx: mx === "found" ? "ok" : "lookup_failed" };
}
